import { readonly, ref } from "vue"
import {
  DEFAULT_WINDOW_HEIGHT,
  DEFAULT_WINDOW_WIDTH,
  cascadePosition,
  maximizedGeometry,
} from "../utils/window_geometry"

const windows = ref([])
let nextZIndex = 1000
let cascadeIndex = 0

function findWindow(id) {
  return windows.value.find((entry) => entry.id === String(id))
}

function openWindow({ id, title, url }) {
  const windowId = String(id)
  const existing = findWindow(windowId)

  if (existing) {
    focusWindow(windowId)
    if (existing.minimized) restoreWindow(windowId)
    return existing
  }

  const position = cascadePosition(cascadeIndex++)
  const entry = {
    id: windowId,
    title: title || "",
    url: url || "#",
    x: position.x,
    y: position.y,
    width: DEFAULT_WINDOW_WIDTH,
    height: DEFAULT_WINDOW_HEIGHT,
    minimized: false,
    maximized: false,
    zIndex: ++nextZIndex,
    preMaximize: null,
  }

  windows.value.push(entry)
  return entry
}

function closeWindow(id) {
  windows.value = windows.value.filter((entry) => entry.id !== String(id))
}

function minimizeWindow(id) {
  const entry = findWindow(id)
  if (!entry) return
  entry.minimized = true
}

function restoreWindow(id) {
  const entry = findWindow(id)
  if (!entry) return
  entry.minimized = false
  focusWindow(id)
}

function maximizeWindow(id) {
  const entry = findWindow(id)
  if (!entry || entry.maximized) return

  entry.preMaximize = {
    x: entry.x,
    y: entry.y,
    width: entry.width,
    height: entry.height,
  }

  Object.assign(entry, maximizedGeometry())
  entry.maximized = true
  focusWindow(id)
}

function unmaximizeWindow(id) {
  const entry = findWindow(id)
  if (!entry || !entry.maximized || !entry.preMaximize) return

  Object.assign(entry, entry.preMaximize)
  entry.maximized = false
  entry.preMaximize = null
  focusWindow(id)
}

function toggleMaximizeWindow(id) {
  const entry = findWindow(id)
  if (!entry) return
  if (entry.maximized) unmaximizeWindow(id)
  else maximizeWindow(id)
}

function focusWindow(id) {
  const entry = findWindow(id)
  if (!entry) return
  entry.zIndex = ++nextZIndex
}

function updateGeometry(id, geometry) {
  const entry = findWindow(id)
  if (!entry || entry.maximized) return
  Object.assign(entry, geometry)
}

export function dispatchOpenContentWindow(detail) {
  document.dispatchEvent(
    new CustomEvent("content-window:open", {
      bubbles: true,
      detail,
    })
  )
}

export function useWindowRegistry() {
  return {
    windows: readonly(windows),
    openWindow,
    closeWindow,
    minimizeWindow,
    restoreWindow,
    maximizeWindow,
    unmaximizeWindow,
    toggleMaximizeWindow,
    focusWindow,
    updateGeometry,
    _resetForTests() {
      windows.value = []
      nextZIndex = 1000
      cascadeIndex = 0
    },
  }
}

export const windowRegistry = useWindowRegistry()
